/* =============================================================================
 * Meraas · collection page (plan 02-plp). Custom elements only — Hyper's own JS
 * (facets.js, theme.js, collection-list.js) is never edited; this file works
 * through the markup hooks it already reads and the events it already fires.
 *   <mg-collection-nav>  sibling collections row (collection-list-slider, mg_style "nav")
 *   <mg-collection>      grid section root (main-collection-product-grid, mg_style "meraas"):
 *                        column switch + sticky-toolbar hairline
 *   <mg-facets>          the ONE facets panel: sticky sidebar ≥1024, drawer below
 * ============================================================================= */
(() => {
  if (window.MgCollection) return;
  window.MgCollection = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isRtl = (el) => getComputedStyle(el).direction === 'rtl';

  /* ------------------------------------------------------------------
   * <mg-collection-nav> — design plp.html:2216-2223 / :2311-2320
   * Brings the current collection into view when it starts off-screen
   * (phones), and shows prev/next arrows only while the row overflows.
   * ------------------------------------------------------------------ */
  class MgCollectionNav extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.coll-nav__list');
      if (!this.list) return;
      this.prevBtn = this.querySelector('.cn-arrow--prev');
      this.nextBtn = this.querySelector('.cn-arrow--next');
      this.onScroll = () => this.updateArrows();
      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.list);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cn]');
        if (button) this.page(Number(button.dataset.cn));
      });
      requestAnimationFrame(() => {
        this.revealCurrent();
        this.updateArrows();
      });
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    revealCurrent() {
      const list = this.list;
      const current = list.querySelector('[aria-current="page"]');
      if (!current) return;
      list.scrollLeft = 0;
      const cs = getComputedStyle(list);
      const box = list.getBoundingClientRect();
      const item = current.getBoundingClientRect();
      const start = box.left + (parseFloat(cs.paddingLeft) || 0);
      const end = box.right - (parseFloat(cs.paddingRight) || 0);
      if (item.left < start - 1 || item.right > end + 1) {
        list.scrollLeft += isRtl(list) ? item.right - end : item.left - start;
      }
    }

    updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      const max = this.list.scrollWidth - this.list.clientWidth;
      const x = Math.abs(this.list.scrollLeft);
      this.prevBtn.hidden = !(max > 2 && x > 2);
      this.nextBtn.hidden = !(max > 2 && x < max - 2);
    }

    /* one full page of whole items */
    page(direction) {
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      this.list.scrollBy({
        left: (this.list.clientWidth + gap) * direction * (isRtl(this.list) ? -1 : 1),
        behavior: reduceMotion() ? 'auto' : 'smooth',
      });
    }
  }
  if (!customElements.get('mg-collection-nav')) customElements.define('mg-collection-nav', MgCollectionNav);
  /* ------------------------------------------------------------------
   * <mg-collection class="collection coll"> — design plp.html:2298-2309
   * Column switch: data-cols-m / data-cols-d live on this root (outside
   * #ProductGridContainer, which facets.js replaces), remembered in
   * localStorage (mg-cols-m / mg-cols-d). Toolbar hairline once it sticks.
   * ------------------------------------------------------------------ */
  class MgCollection extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.viewas [data-cols]');
        if (!button || !this.contains(button)) return;
        const mobile = button.classList.contains('viewas--m');
        this.dataset[mobile ? 'colsM' : 'colsD'] = button.dataset.cols;
        try {
          localStorage.setItem(mobile ? 'mg-cols-m' : 'mg-cols-d', button.dataset.cols);
        } catch (e) {}
        this.syncPressed();
      });
      this.syncPressed();

      this.toolbar = this.querySelector('.toolbar--sticky');
      if (this.toolbar) {
        this.onScroll = () => {
          const top = parseInt(getComputedStyle(this.toolbar).top, 10) || 0;
          const stuck = this.toolbar.getBoundingClientRect().top <= top + 1 && window.scrollY > 200;
          this.toolbar.classList.toggle('is-stuck', stuck);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    syncPressed() {
      this.querySelectorAll('.viewas [data-cols]').forEach((button) => {
        const value = button.classList.contains('viewas--m') ? this.dataset.colsM : this.dataset.colsD;
        button.setAttribute('aria-pressed', String(button.dataset.cols === value));
      });
    }
  }
  if (!customElements.get('mg-collection')) customElements.define('mg-collection', MgCollection);

  /* ------------------------------------------------------------------
   * <mg-facets id="FacetFiltersContainer" class="facets"> — design :1564 / :1886-1897 / :2303-2307
   * Below 1024 (or when the section's filter type is "drawer") the panel is a
   * modal drawer: scrim, Esc, focus trap (FoxTheme.a11y). facets.js replaces
   * this element's innerHTML after every filter change, so state lives on the
   * element itself, clicks are delegated, and focus is put back afterwards.
   * Price fields apply 500ms after typing stops (design :2282), not on blur.
   * ------------------------------------------------------------------ */
  const PRICE_INPUT = 'input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]';

  class MgFacets extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.coll') || document;
      this.scrim = this.parentElement.querySelector(':scope > .scrim');
      this.desktop = window.matchMedia('(min-width: 1024px)');

      this.onClick = (event) => {
        const opener = event.target.closest('[data-facets-open]');
        if (opener && this.root.contains(opener)) {
          event.preventDefault();
          this.open(opener);
          return;
        }
        const closer = event.target.closest('[data-facets-close]');
        if (closer && this.isOpen && (this.contains(closer) || closer === this.scrim)) {
          event.preventDefault();
          this.close();
        }
      };
      this.onKey = (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      };
      this.onMedia = () => {
        this.syncRole();
        if (this.isSidebar && this.isOpen) this.close(false);
      };
      this.onRerender = () => this.restoreFocus();

      document.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('collection:rerendered', this.onRerender);
      this.desktop.addEventListener('change', this.onMedia);
      this.addEventListener('change', this.onChangeCapture.bind(this), true);
      this.addEventListener('input', this.onInput.bind(this));
      this.syncRole();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('collection:rerendered', this.onRerender);
      this.desktop.removeEventListener('change', this.onMedia);
      document.body.classList.remove('mg-facets-open');
    }

    get isSidebar() {
      return this.desktop.matches && !this.root.classList.contains('coll--drawer');
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get openers() {
      return this.root.querySelectorAll('[data-facets-open]');
    }

    syncRole() {
      if (this.isSidebar) {
        this.removeAttribute('role');
        this.removeAttribute('aria-modal');
      } else {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
      }
    }

    open(trigger) {
      if (this.isSidebar || this.isOpen) return;
      this.trigger = trigger;
      this.classList.add('is-open');
      if (this.scrim) this.scrim.classList.add('is-open');
      document.body.classList.add('mg-facets-open');
      this.openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.trap(this.querySelector('.facets__x') || this);
    }

    close(returnFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      if (this.scrim) this.scrim.classList.remove('is-open');
      document.body.classList.remove('mg-facets-open');
      this.openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (window.FoxTheme && FoxTheme.a11y) FoxTheme.a11y.removeTrapFocus();
      if (returnFocus && this.trigger) this.trigger.focus({ preventScroll: true });
    }

    trap(element) {
      if (window.FoxTheme && FoxTheme.a11y) FoxTheme.a11y.trapFocus(this, element);
      else element.focus();
    }

    /* remember what was used, and swallow the native "change" that would re-apply a debounced price */
    onChangeCapture(event) {
      const target = event.target;
      if (target.id) this.focusId = target.id;
      if (event.isTrusted && target.matches(PRICE_INPUT) && target.dataset.mgSent === target.value) {
        event.stopImmediatePropagation();
      }
    }

    onInput(event) {
      const target = event.target;
      if (!target.matches(PRICE_INPUT)) return;
      clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => {
        if (target.dataset.mgSent === target.value) return;
        target.dataset.mgSent = target.value;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }, 500);
    }

    restoreFocus() {
      const element = this.focusId && document.getElementById(this.focusId);
      if (this.isOpen) this.trap(element || this.querySelector('.facets__x') || this);
      else if (element && this.contains(element)) element.focus({ preventScroll: true });
    }
  }
  if (!customElements.get('mg-facets')) customElements.define('mg-facets', MgFacets);
})();
